import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

function PartnerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const snap = await getDoc(doc(db, 'partners', id));
        if (snap.exists()) {
          setPartner({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error('Error fetching partner:', err);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchPartner();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, 'partners', id), { status: newStatus });
      setPartner(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Error updating partner:', err);
    }
  };

  if (loading) return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-golden"></div>
      </div>
    </div>
  );

  if (!partner) return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}>
        <div className="text-center">
          <p className="text-gray-500 text-lg">Partner not found</p>
          <button onClick={() => navigate('/admin/partners')} className="mt-4 text-brand-golden hover:underline">Back to Partners</button>
        </div>
      </div>
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-y-auto transition-all duration-300" style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}>
        <div className="p-8 max-w-4xl">
          <div className="flex items-center mb-6">
            {isMobile && (
              <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
            )}
            <button onClick={() => navigate('/admin/partners')} className="mr-4 text-gray-400 hover:text-brand-golden">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Partner Details</h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-brand-dark p-8">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-brand-golden/20 flex items-center justify-center text-3xl font-bold text-white">
                  {partner.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{partner.name}</h2>
                  <p className="text-gray-300">{partner.company}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(partner.status)}`}>{partner.status?.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Email</label><p className="text-gray-900 mt-1">{partner.email}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Phone</label><p className="text-gray-900 mt-1">{partner.phone}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Location</label><p className="text-gray-900 mt-1">{partner.location}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Joined</label><p className="text-gray-900 mt-1">{partner.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p></div>
              {partner.rejectionReason && <div className="col-span-2"><label className="text-xs font-semibold text-red-400 uppercase">Rejection Reason</label><p className="text-red-700 mt-1">{partner.rejectionReason}</p></div>}
            </div>

            <div className="px-8 pb-8 flex gap-3">
              {partner.status === 'pending' && (
                <>
                  <button onClick={() => handleStatusChange('approved')} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Approve</button>
                  <button onClick={() => handleStatusChange('rejected')} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Reject</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerDetails;
